import { defineStore } from 'pinia';
import { shouldThrottle } from '@/util/rateLimit.js';
import posthog from '@/composables/usePostHog.js';
import apiClient from '@/util/apiClient.js';
import { asFiniteNumber } from '@/util/numbers';
import type { ApiClient } from '@/types/http';
import type { MatchFeeLookupMatch, MatchFeeSchedule, MatchFeeScheduleClientRef, MatchFeeScheduleGroupRef } from '@/types/matchFees';

const http = apiClient as ApiClient;

export interface BulkMatchFeeSchedule {
	groupName: string;
	clientNumber: number | null;
	league: string;
	season: string | null;
	division: string | null;
	level: string;
	crFee: number;
	ar1Fee: number;
	ar2Fee: number;
	fourthFee: number;
	mentorFee: number;
	assignorFee: number;
	groupFee: number;
}

export interface BulkMatchFeeScheduleRequest {
	schedules: BulkMatchFeeSchedule[];
}

function asNullableNumber(value: unknown) {
	if (value == null || value === '') return null;
	const numeric = Number(value);
	return Number.isFinite(numeric) ? numeric : null;
}

function asNullableString(value: unknown) {
	if (typeof value === 'string') return value;
	if (typeof value === 'number' || typeof value === 'boolean') return String(value);
	if (value == null) return null;
	return null;
}

function normalizeClientRef(value: unknown): MatchFeeScheduleClientRef | null {
	if (!value || typeof value !== 'object') return null;
	const ref = value as Record<string, unknown>;
	return {
		clientNumber: asNullableNumber(ref['clientNumber']),
		name: asNullableString(ref['name']),
		shortName: asNullableString(ref['shortName'])
	};
}

function normalizeGroupRef(value: unknown): MatchFeeScheduleGroupRef | null {
	if (!value || typeof value !== 'object') return null;
	const ref = value as Record<string, unknown>;
	return {
		groupNumber: asNullableNumber(ref['groupNumber']),
		name: asNullableString(ref['name'])
	};
}

function normalizeSchedule(value: unknown): MatchFeeSchedule | null {
	if (!value || typeof value !== 'object') return null;
	const schedule = value as Record<string, unknown>;
	const normalizedClient = normalizeClientRef(schedule['clientNumber'] ?? schedule['client']);
	const normalizedGroup = normalizeGroupRef(schedule['groupNumber'] ?? schedule['group']);

	return {
		...schedule,
		scheduleNumber: asNullableNumber(schedule['scheduleNumber']),
		clientNumber: normalizedClient ?? asNullableNumber(schedule['clientNumber']),
		client: normalizeClientRef(schedule['client']) ?? normalizedClient,
		groupNumber: normalizedGroup ?? asNullableNumber(schedule['groupNumber']),
		group: normalizeGroupRef(schedule['group']) ?? normalizedGroup,
		league: asNullableString(schedule['league']),
		level: asNullableString(schedule['level']),
		division: asNullableString(schedule['division']),
		season: asNullableString(schedule['season']),
		crFee: asNullableNumber(schedule['crFee']),
		ar1Fee: asNullableNumber(schedule['ar1Fee']),
		ar2Fee: asNullableNumber(schedule['ar2Fee']),
		fourthFee: asNullableNumber(schedule['fourthFee']),
		mentorFee: asNullableNumber(schedule['mentorFee']),
		assignorFee: asNullableNumber(schedule['assignorFee']),
		groupFee: asNullableNumber(schedule['groupFee']),
		createdOn: asNullableString(schedule['createdOn'])
	};
}

function normalizeSchedules(value: unknown): MatchFeeSchedule[] {
	return Array.isArray(value) ? value.map(normalizeSchedule).filter((schedule): schedule is MatchFeeSchedule => schedule != null) : [];
}

function scheduleClientNumber(schedule: MatchFeeSchedule) {
	if (schedule.clientNumber && typeof schedule.clientNumber === 'object') {
		return schedule.clientNumber.clientNumber;
	}
	return schedule.clientNumber == null ? null : asFiniteNumber(schedule.clientNumber, 0);
}

function normalizeGroupNumber(value: unknown) {
	const groupNumber = asNullableNumber(value);
	return groupNumber != null && groupNumber > 0 ? groupNumber : null;
}

function cacheKeyForClientIds(clientIds: Array<number | string | null | undefined>) {
	return clientIds
		.map((id) => normalizeGroupNumber(id))
		.filter((id): id is number => id != null)
		.sort((left, right) => left - right)
		.join(',');
}

function normalizedString(value: string | null | undefined) {
	return (value ?? '').toLowerCase().trim();
}

function scheduleCreatedOnValue(schedule: MatchFeeSchedule) {
	const raw = schedule.createdOn ?? null;
	if (!raw) return 0;
	const parsed = Date.parse(raw);
	return Number.isFinite(parsed) ? parsed : 0;
}

function scheduleSpecificityScore(schedule: MatchFeeSchedule) {
	let score = 0;
	if (scheduleClientNumber(schedule) != null) score++;
	if (normalizedString(schedule.league)) score++;
	if (normalizedString(schedule.season)) score++;
	if (normalizedString(schedule.division)) score++;
	if (normalizedString(schedule.level)) score++;
	return score;
}

function captureStoreError(action: string, context: Record<string, unknown>, error: unknown) {
	posthog.capture('match_fee_store_error', {
		action,
		...context,
		message: error instanceof Error ? error.message : 'Unknown error'
	});
}

export const useMatchFeesStore = defineStore('matchFeesStore', {
	state: () => ({
		matchFees: [] as MatchFeeSchedule[],
		feesByGroup: {} as Record<number, MatchFeeSchedule[]>,
		feesByClient: [] as MatchFeeSchedule[]
	}),
	actions: {
		clearFeeScheduleCaches() {
			this.matchFees = [];
			this.feesByGroup = {};
			this.feesByClient = [];
		},

		async fetchAllMatchFees() {
			if (shouldThrottle('fetchAllMatchFees', 100)) {
				posthog.capture('throttled_action', { action: 'fetchAllMatchFees' });
				return this.matchFees;
			}
			const response = await http.get<unknown>('/fee/schedule/all');
			this.matchFees = normalizeSchedules(response.data);
			return this.matchFees;
		},

		async fetchFeesByGroup(groupNumber: number | string | null | undefined, options: { force?: boolean } = {}) {
			const normalizedGroupNumber = normalizeGroupNumber(groupNumber);
			if (normalizedGroupNumber == null) return [];

			if (!options.force && shouldThrottle(`fetchFeesByGroup:${String(normalizedGroupNumber)}`, 100)) {
				posthog.capture('throttled_action', { action: 'fetchFeesByGroup', groupNumber: normalizedGroupNumber });
				return this.feesByGroup[normalizedGroupNumber] ?? [];
			}

			if (!options.force && this.feesByGroup[normalizedGroupNumber]) {
				return this.feesByGroup[normalizedGroupNumber];
			}

			try {
				const response = await http.get<unknown>(`/fee/schedule/group/${String(normalizedGroupNumber)}`);
				const schedules = normalizeSchedules(response.data);
				this.feesByGroup[normalizedGroupNumber] = schedules;
				return schedules;
			} catch (error) {
				captureStoreError('fetchFeesByGroup', { groupNumber: normalizedGroupNumber }, error);
				this.feesByGroup[normalizedGroupNumber] = [];
				return [];
			}
		},

		async fetchFeesForClient(clientIds: Array<number | string | null | undefined>) {
			const normalizedClientIds = clientIds.map((id) => normalizeGroupNumber(id)).filter((id): id is number => id != null);
			if (!normalizedClientIds.length) return [];

			const cacheKey = cacheKeyForClientIds(normalizedClientIds);
			if (shouldThrottle(`fetchFeesForClient:${cacheKey}`, 100)) {
				posthog.capture('throttled_action', { action: 'fetchFeesForClient', clientIds: normalizedClientIds });
				return this.feesByClient;
			}

			try {
				const response = await http.post<unknown>('/fee/schedule/client', normalizedClientIds);
				this.feesByClient = normalizeSchedules(response.data);
				return this.feesByClient;
			} catch (error) {
				captureStoreError('fetchFeesForClient', { clientIds: normalizedClientIds }, error);
				this.feesByClient = [];
				return [];
			}
		},

		async fetchFeesForGroups(groupNumbers: Array<number | string | null | undefined>, options: { force?: boolean } = {}) {
			const uniqueGroups = [...new Set(groupNumbers.map((groupNumber) => normalizeGroupNumber(groupNumber)).filter((groupNumber): groupNumber is number => groupNumber != null))];
			await Promise.all(uniqueGroups.map((groupNumber) => this.fetchFeesByGroup(groupNumber, options)));
		},

		findFeesForMatch(match: MatchFeeLookupMatch | null | undefined, candidateSchedules: MatchFeeSchedule[] | null = null) {
			const groupNumber = normalizeGroupNumber(match?.group?.groupNumber);
			const groupFees = Array.isArray(candidateSchedules) ? candidateSchedules : groupNumber == null ? [] : (this.feesByGroup[groupNumber] ?? []);
			if (!groupFees.length) return null;

			const clientNumber = normalizeGroupNumber(match?.client?.clientNumber);
			const level = normalizedString(match?.level);
			const league = normalizedString(match?.league);
			const division = normalizedString(match?.division);
			const season = normalizedString(match?.season);

			return (
				groupFees
					.filter((schedule) => {
						const scheduleClient = scheduleClientNumber(schedule);
						if (scheduleClient != null && scheduleClient !== clientNumber) return false;

						const scheduleLevel = normalizedString(schedule.level);
						if (scheduleLevel && scheduleLevel !== level) return false;

						const scheduleLeague = normalizedString(schedule.league);
						if (scheduleLeague && scheduleLeague !== league) return false;

						const scheduleDivision = normalizedString(schedule.division);
						if (scheduleDivision && scheduleDivision !== division) return false;

						const scheduleSeason = normalizedString(schedule.season);
						return !scheduleSeason || scheduleSeason === season;
					})
					.sort((left, right) => {
						// Prefer the most specific schedule first, then the newest created schedule, and finally the
						// highest scheduleNumber as a last-resort proxy for recency when timestamps are missing.
						const specificityDelta = scheduleSpecificityScore(right) - scheduleSpecificityScore(left);
						if (specificityDelta !== 0) return specificityDelta;

						const createdOnDelta = scheduleCreatedOnValue(right) - scheduleCreatedOnValue(left);
						if (createdOnDelta !== 0) return createdOnDelta;

						return asFiniteNumber(right.scheduleNumber, 0) - asFiniteNumber(left.scheduleNumber, 0);
					})[0] ?? null
			);
		},

		getMatchFees(match: MatchFeeLookupMatch | null | undefined, candidateSchedules: MatchFeeSchedule[] | null = null) {
			const schedule = this.findFeesForMatch(match, candidateSchedules);
			if (!schedule) return null;

			return {
				crFee: schedule.crFee ?? null,
				ar1Fee: schedule.ar1Fee ?? null,
				ar2Fee: schedule.ar2Fee ?? null,
				fourthFee: schedule.fourthFee ?? null,
				mentorFee: schedule.mentorFee ?? null
			};
		},

		async addMatchFee(matchFee: unknown) {
			if (shouldThrottle('addMatchFee', 100)) {
				posthog.capture('throttled_action', { action: 'addMatchFee' });
				return;
			}
			const response = await http.post<unknown>('/fee/schedule/save', matchFee);
			this.clearFeeScheduleCaches();
			return response.data;
		},
		async uploadMatchFees(payload: BulkMatchFeeScheduleRequest) {
			if (shouldThrottle('uploadMatchFees', 1000)) {
				posthog.capture('throttled_action', {
					action: 'uploadMatchFees'
				});

				throw new Error('The match fee upload request was submitted too quickly.');
			}

			if (!Array.isArray(payload.schedules) || payload.schedules.length === 0) {
				throw new Error('At least one match fee schedule is required.');
			}

			return apiClient.post('/match/fees/saveAll', payload);
		},
		async updateMatchFee(matchFee: { scheduleNumber: number | null }) {
			if (shouldThrottle('updateMatchFee', 100)) {
				posthog.capture('throttled_action', { action: 'updateMatchFee' });
				return;
			}
			if (matchFee.scheduleNumber == null) return;
			const response = await http.patch<unknown>(`/fee/schedule/edit/${String(matchFee.scheduleNumber)}`, matchFee);
			this.clearFeeScheduleCaches();
			return response.data;
		},

		async deleteMatchFee(settingId: number | string | null | undefined) {
			const normalizedSettingId = normalizeGroupNumber(settingId);
			if (normalizedSettingId == null) return;
			if (shouldThrottle('deleteMatchFee', 100)) {
				posthog.capture('throttled_action', { action: 'deleteMatchFee' });
				return;
			}
			await http.delete<unknown>(`/fee/schedule/delete/${String(normalizedSettingId)}`);
			this.clearFeeScheduleCaches();
		}
	}
});
